import { createSelector } from '@reduxjs/toolkit';

import type { BrandKit, Project } from 'js/src/client';
import { RoleEnum } from 'js/src/client';
import { getAIVoiceById } from 'js/src/data/ai-voices-and-avatars/ai-voices/ai-voices-selectors';
import { getBlueprint } from 'js/src/data/blueprints/blueprints-selectors';
import { getBrandKitById } from 'js/src/data/brand-kits/brand-kits-selectors';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import { getTemplateById } from 'js/src/data/templates/templates-selectors';
import type { StoredTemplate } from 'js/src/data/templates';
import type { DeepReadonly } from 'js/src/libs/consts';
import { withStableEmptyArray } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { getServerData } from 'js/src/libs/server-data';

const NO_PROJECT_OPTIONS: readonly DeepReadonly<Project>[] = [];

export const selectSketchControls = (state: RootState) => state.sketchControls;

export const selectSketchControlsTemplate = (state: RootState): StoredTemplate | null => {
    const controls = state.sketchControls;
    if (!controls?.templateId) return null;

    const template = getTemplateById(state, controls.templateId);
    return template || null;
};

export const selectSketchControlsVoiceName = (state: RootState): string | null => {
    const controls = state.sketchControls;
    if (!controls?.voiceId) return null;

    const voice = getAIVoiceById(state, controls.voiceId);
    return voice?.display_name || null;
};

export const selectSketchControlsBlueprintId = (state: RootState): number | null => {
    return state.sketchControls?.blueprintId ?? null;
};

export const selectSketchControlsBlueprintName = (state: RootState): string | null => {
    const controls = state.sketchControls;
    if (!controls?.blueprintId) return null;

    const blueprint = getBlueprint(state, controls.blueprintId);
    return blueprint?.name || null;
};

export const selectSketchControlsBlueprint = (state: RootState) => {
    const controls = state.sketchControls;
    if (!controls?.blueprintId) return null;

    return getBlueprint(state, controls.blueprintId);
};

export const selectSketchControlsBrandKitId = (state: RootState): number | null => {
    return state.sketchControls?.brandKitId ?? null;
};

export const selectSketchControlsBrandKit = (state: RootState): DeepReadonly<BrandKit> | null => {
    const controls = state.sketchControls;
    const brandKitId = controls?.brandKitId;
    if (brandKitId === null || brandKitId === undefined) return null;
    const brandKit = getBrandKitById(state, brandKitId);
    return brandKit || null;
};

export const selectSketchControlsBrandKitName = (state: RootState): string | null => {
    const controls = state.sketchControls;
    const brandKitId = controls?.brandKitId;
    if (brandKitId === null || brandKitId === undefined) return null;
    const brandKit = getBrandKitById(state, brandKitId);
    return brandKit?.name || null;
};

export const selectSketchControlsProjectId = (state: RootState): number | null => {
    return state.sketchControls?.projectId ?? null;
};

/** Editors' videos must belong to a project; admins may create at the workspace's top level. */
export function isVideoCreationProjectRequired(): boolean {
    return getServerData()?.USER?.role === RoleEnum.EDITOR;
}

/**
 * The projects the user can create a video in: the active workspace's non-archived projects,
 * by name. Admins receive projects from every workspace they belong to, so filter here.
 */
export const selectVideoCreationProjectOptions = createSelector(
    [
        (state: RootState) => state.account?.projects,
        () => getServerData()?.USER?.workspace_id ?? null,
    ],
    (projects, workspaceId): readonly DeepReadonly<Project>[] => {
        if (!projects || workspaceId === null) return NO_PROJECT_OPTIONS;
        const options = new PlainJsStoreORM<DeepReadonly<Project>, number>(projects)
            .filter((project) => project.workspace === workspaceId && !project.archived)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        return withStableEmptyArray(options);
    },
);

/**
 * The project the next video lands in: the picked one while it is still available, otherwise
 * the newest project the user belongs to when a project is required, otherwise none.
 */
export const selectVideoCreationProjectId = createSelector(
    [
        selectSketchControlsProjectId,
        selectVideoCreationProjectOptions,
        () => isVideoCreationProjectRequired(),
    ],
    (pickedProjectId, options, required): number | null => {
        if (pickedProjectId !== null && options.some((project) => project.id === pickedProjectId)) {
            return pickedProjectId;
        }
        if (!required || options.length === 0) return null;
        return options.reduce((newest, project) => Math.max(newest, project.id), -Infinity);
    },
);
